import './policy-summary.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse } from 'antd';
import CollapsePanel from 'antd/es/collapse/CollapsePanel';
import CustomIcon, { IconType } from '../../shared/custom-icon/custom-icon';
import Arrow from '@svg/chevron.svg?react';
import { LevelName, ProtectionMenuNameOptions } from '../../../utils/enums';
import { PolicySummaryDto } from '../../../dtos/policy-summary.dto';
import {
    getAppsSummary,
    getFirewallRulesSummary,
    getGeoFilteringSummary,
    getIpControlSummary,
    getLibrarySummary,
    getServicesSummary,
    getThreatProtectionSummary,
    getWebFilteringSummary,
    PolicySummaryPanelData
} from './policy-summary-utils';
import ListDisplay from './list-display';
import GeoFilteringContent from './geo-filtering-content';
import ThreatsContent from './threats-content';
import AppsContent from './apps-content';
import Loader from '../../shared/loader/loader';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import { useStores } from '../../store';

interface PolicySummaryProps {
    children?: JSX.Element;
    serviceLevel?: LevelName;
    externalId: string;
    connectionId: number;
    comments?: string;
}

export const PolicySummary: React.FC<PolicySummaryProps> = observer(({ children, serviceLevel, externalId, connectionId, comments }) => {

    const {fwVersionStore} = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<PolicySummaryDto>();

    useEffect(() => {
        if (externalId) {
            setIsLoading(true);
            promiseFeedbackFactory({
                promise: fwVersionStore.getPolicySummary(externalId, connectionId),
                successCallback: (res) => {
                    setData(res);
                    setIsLoading(false);
                },
                errorCallback: () => setIsLoading(false),
                notifObj: {show: false}
            });
        }
    }, [externalId]);

    const items: {
        [key in ProtectionMenuNameOptions]: {
            key: string,
            icon: IconType;
            summaryTitle: string;
            data: PolicySummaryPanelData,
            ContentRenderer?: React.FC<{ data: PolicySummaryPanelData }>;
        }
    } | undefined = useMemo(() => {
        if (!data) {
            return undefined;
        }
        const geoSummary = getGeoFilteringSummary(data);
        const webSummary = getWebFilteringSummary(data);
        const appsSummary = getAppsSummary(data);
        const servicesSummary = getServicesSummary(data);
        const ipControlSummary = getIpControlSummary(data);
        const threatProtectionSummary = getThreatProtectionSummary(data, serviceLevel);
        const firewallRulesSummary = getFirewallRulesSummary(data);
        const librarySummary = getLibrarySummary(data);

        return {
            [ProtectionMenuNameOptions.GEO_FILTERING]: {
                key: 'geo-filtering',
                icon: IconType.GEO_FILTERING,
                summaryTitle: 'Geo Filtering',
                data: geoSummary,
                ContentRenderer: GeoFilteringContent
            },
            [ProtectionMenuNameOptions.WEB_FILTERING]: {
                key: 'web-filtering',
                icon: IconType.WEB_FILTERING,
                summaryTitle: 'Web Filtering',
                data: webSummary
            },
            [ProtectionMenuNameOptions.APPS]: {
                key: 'apps',
                icon: IconType.APPS_LOGO,
                summaryTitle: 'Apps',
                data: appsSummary,
                ContentRenderer: AppsContent
            },
            [ProtectionMenuNameOptions.SERVICES]: {
                key: 'services',
                icon: IconType.SERVICES_LOGO,
                summaryTitle: 'Services',
                data: servicesSummary
            },
            [ProtectionMenuNameOptions.IP_CONTROL]: {
                key: 'ips',
                icon: IconType.IP_CONTROL,
                summaryTitle: 'IPs',
                data: ipControlSummary
            },
            [ProtectionMenuNameOptions.PROTECTION_PLUS]: {
                key: 'threats',
                icon: IconType.PROTECTION_PLUS,
                summaryTitle: 'Protection +',
                data: threatProtectionSummary,
                ContentRenderer: ThreatsContent
            },
            [ProtectionMenuNameOptions.FIREWALL_RULES]: {
                key: 'firewall-rules',
                icon: IconType.FIREWALL,
                summaryTitle: 'Firewall Rules',
                data: firewallRulesSummary
            },
            [ProtectionMenuNameOptions.Library]: {
                key: 'library',
                icon: IconType.INVENTORY,
                summaryTitle: 'Library',
                data: librarySummary
            }
        };
    }, [data]);

    return <>
        <div className="policy-summary">
            {isLoading ? <Loader type={'component'}/> : <>
                {children}
                <div className="policy-summary-description">{comments}</div>
                <Collapse className={'policy-summary-content'} bordered={false}>
                    {items && Object.values(items).map((item) => (
                        <CollapsePanel
                            showArrow={false}
                            key={item.key}
                            header={
                                <div className="collapse-header-wrapper">
                                    <span className="collapse-header-icon"><CustomIcon type={item.icon}/></span>
                                    <div className="collapse-header-info">
                                        <div className="collapse-header-text">{item.summaryTitle}</div>
                                        <div className="collapse-header-summary">
                                            {item.data.headers.map((_, index) => (
                                                <>
                                                    <span>{_}</span>
                                                    {index < item.data.headers.length - 1 ?
                                                        <span className={'bullet'}>•</span> : null}
                                                </>
                                            ))}
                                        </div>
                                    </div>
                                    <Arrow/>
                                </div>
                            }
                        >
                            <div className={`${item.key}-panel-content`}>
                                {item.ContentRenderer ? <item.ContentRenderer data={item.data}/> :
                                    <ListDisplay lists={item.data.lists}/>}
                            </div>
                        </CollapsePanel>
                    ))}
                </Collapse>
            </>}
        </div>
        {promiseContextHolder}
    </>;
});
